import pickBy from "lodash/pickBy";
import { getPriceOrDiscounted } from "@Components/cart/helpers/helpers";
import { ICTProductDataCurrent } from "@Types/product";
import { convertToDollar } from "@Utils/currency";
import { ICartLineItem } from "@wff/interfaces/cart";
import { ICartState } from "@wff/store/cartSlice";

interface IIdentifyParams {
  $email: string;
  $first_name: string;
  $last_name: string;
}

declare global {
  interface Window {
    klaviyo: {
      push: (params: any) => Promise<any>;
      identify: (_params: IIdentifyParams) => Promise<boolean>;
    };
  }
}

const sendKlaviyoAnalytics = (params: any[]) => {
  window?.klaviyo?.push(params);
};

const removeFalsyValues = (obj: any) => {
  return pickBy(obj);
};

const processLineItems = (
  lineItems: ICartLineItem[],
  locale: string,
  url: string
) => {
  const items: {}[] = [];
  const itemNames: string[] = [];

  lineItems?.forEach((item) => {
    itemNames.push(item.name);
    items.push({
      ProductID: item.sku,
      SKU: item.sku,
      ProductName: item.name,
      Quantity: item.quantity,
      ItemPrice: convertToDollar(item.totalPrice / item.quantity),
      RowTotal: convertToDollar(item.totalPrice),
      ProductURL: `${new URL(url).origin}/${locale}/products/${item.slug}/`,
      ImageURL: item.productImageUrl,
      ProductCategories: [],
    });
  });

  return { itemNames, items };
};

export const getFormattedProductForKlaviyo = (
  currentProduct: ICTProductDataCurrent,
  locale: string,
  url: string
) => {
  const priceData = currentProduct?.masterVariant?.prices;
  const imageUrl = currentProduct?.masterVariant?.images?.[0]?.url;
  const totalPrice = priceData ? getPriceOrDiscounted(priceData) : "";
  const productName = currentProduct?.name?.[locale];

  const formattedProduct = {
    ProductName: productName,
    ProductID: currentProduct?.masterVariant?.sku,
    Price: totalPrice,
    Categories: [],
    CompareAtPrice: "",
    ImageUrl: imageUrl,
    Brand: "",
    Url: url,
    Locale: locale,
  };

  return removeFalsyValues(formattedProduct);
};

export const getFormattedCheckoutDataForKlaviyo = (
  cartData: ICartState,
  locale: string,
  url: string
) => {
  const { lineItems = [] } = cartData;
  const { itemNames, items } = processLineItems(lineItems, locale, url);

  const formattedCartData = {
    $value: convertToDollar(cartData?.totalPrice),
    ItemNames: itemNames,
    CheckoutURL: url,
    Categories: [],
    Items: items,
    Locale: locale,
  };

  return removeFalsyValues(formattedCartData);
};

export const getFormattedAddToCartDataForKlaviyo = (
  currentProduct: ICTProductDataCurrent,
  cartData: ICartState,
  locale: string,
  url: string
) => {
  const priceData = currentProduct?.masterVariant?.prices;
  const productName = currentProduct?.name?.[locale] ?? "";
  const productSlug = currentProduct?.slug?.[locale] ?? "";
  const productSku = currentProduct?.masterVariant?.sku ?? "";
  const productPrice = priceData ? getPriceOrDiscounted(priceData) : 0;
  const totalPrice = convertToDollar(cartData.totalPrice) + productPrice;
  const productImageUrl = currentProduct?.masterVariant?.images?.[0]?.url ?? "";
  const baseURL = new URL(url).origin;
  const checkoutURL = `${baseURL}/${locale}/checkout`;
  const currentLineItem: ICartLineItem = {
    id: "",
    name: productName,
    weightText: "",
    perUnitPriceText: "",
    itemPiecesText: "",
    totalPrice: productPrice * 100,
    quantity: 1,
    productImageUrl,
    sku: productSku,
    slug: productSlug,
    isFixedPrice: false,
  };
  const { lineItems = [] } = cartData;
  const { itemNames, items } = processLineItems(
    [...lineItems, currentLineItem],
    locale,
    url
  );

  const formattedProduct = {
    $value: totalPrice,
    AddedItemProductName: productName,
    AddedItemProductID: productSku,
    AddedItemSKU: productSku,
    AddedItemCategories: [],
    AddedItemURL: url,
    AddedItemPrice: productPrice,
    AddedItemQuantity: 1,
    ItemNames: itemNames,
    CheckoutURL: checkoutURL,
    Items: items,
  };

  return removeFalsyValues(formattedProduct);
};

export const isIdentifyKlaviyoUser = (_paramObj: IIdentifyParams) => {
  return window?.klaviyo?.identify(_paramObj);
};

export const sendTrackKlaviyoEvent = (params: any) =>
  sendKlaviyoAnalytics(["track", ...params]);

export const sendViewedItemKlaviyoEvent = (data: any) =>
  sendKlaviyoAnalytics(["trackViewedItem", data]);
